"use client";

import React from "react";
import { MapPin, Phone, Mail, Terminal, ShieldAlert, Cpu } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const contactInfo = [{
  icon: MapPin,
  label: "Headquarters",
  value: "450 Justice Plaza, Metro City",
  description: "Main Administrative Office"
}, {
  icon: Phone,
  label: "Non-Emergency",
  value: "(555) 010-3111",
  description: "General Inquiries & Records"
}, {
  icon: Mail,
  label: "Official Email",
  value: "info@metrocentralpd.gov",
  description: "Administrative Correspondence"
}];
const inputBaseClasses = "flex w-full bg-transparent border-0 border-b border-border py-3 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 rounded-md font-default";
function ExpeditionFormInternal() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center border border-primary/20 bg-accent/50 rounded-lg shadow-md">
        <ShieldAlert className="size-12 text-primary mb-4" />
        <h3 className="text-2xl font-default font-bold text-foreground mb-2">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground font-default mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default border-primary/50 hover:bg-primary hover:text-primary-foreground">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required for official records."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-primary tracking-wide uppercase text-xs">
                Full Legal Name
              </FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="John A. Smith" className={inputBaseClasses} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                Please provide your name as it appears on government ID.
              </FormDescription>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Contact email is required.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-primary tracking-wide uppercase text-xs">
                Contact Email Address
              </FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="jsmith@example.com" className={inputBaseClasses} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                We will send follow-up documentation to this address.
              </FormDescription>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="phone_number" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-semibold text-primary tracking-wide uppercase text-xs">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <input {...field} type="text" placeholder="(555) 000-0000" className={inputBaseClasses} />
                </FormControl>
                <FormDescription className="font-default text-xs opacity-70">
                  Optional contact number.
                </FormDescription>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />

          <FormField control={control} name="department" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-semibold text-primary tracking-wide uppercase text-xs">
                  Target Department
                </FormLabel>
                <FormControl>
                  <select {...field} className={`${inputBaseClasses} appearance-none cursor-pointer`}>
                    <option value="" disabled className="bg-background text-muted-foreground">
                      Select Department...
                    </option>
                    {["Records Division", "Investigations", "Animal Control", "Traffic Safety", "Other"].map((option, i) => <option key={i} value={option} className="bg-background text-foreground">
                          {option}
                        </option>)}
                  </select>
                </FormControl>
                <FormDescription className="font-default text-xs opacity-70">
                  Direct your inquiry to the correct unit.
                </FormDescription>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />
        </div>

        <FormField control={control} name="interest_message" rules={{
        maxLength: {
          value: 1000,
          message: "Message exceeds maximum length."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-primary tracking-wide uppercase text-xs">
                Inquiry Details
              </FormLabel>
              <FormControl>
                <textarea {...field} placeholder="Please provide a detailed description of your request or report..." className={`${inputBaseClasses} min-h-[120px] resize-none`} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 rounded-md">
          <p className="text-destructive font-default text-sm flex items-center gap-2">
            <Terminal className="size-4" />
            Submission Error: {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 font-default font-bold text-lg tracking-wide uppercase relative overflow-hidden group bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all duration-300">
        <span className="relative z-10 flex items-center gap-2">
          {isSubmitting ? <>
              <Cpu className="animate-spin size-5" />
              Processing...
            </> : <>
              <ShieldAlert className="size-5" />
              Submit Official Inquiry
            </>}
        </span>
        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out" />
      </Button>
    </Form>;
}
export default function ReachOurTeam() {
  return <section data-section-id="225268" id="reach-our-team" className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-24 items-start">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-primary/20 bg-accent text-primary font-default font-semibold text-sm mb-6">
                <ShieldAlert className="size-4" />
                <span>Official Channel</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-extrabold text-primary mb-4">
                Reach Our Team
              </h2>
              <p className="text-muted-foreground font-default text-base leading-relaxed">
                For non-emergency matters, please use the directory below or submit a formal inquiry. 
                Our administrative staff monitors these channels during standard business hours. 
                <strong> For emergencies, dial 911 immediately.</strong>
              </p>
            </div>

            <div className="space-y-8">
              {contactInfo.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-4 group">
                  <div className="p-3 rounded-md bg-primary/5 border border-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground shadow-sm transition-all duration-300">
                    <item.icon className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-default font-bold text-sm text-primary tracking-wider uppercase mb-1">
                      {item.label}
                    </h4>
                    <p className="text-lg font-medium text-foreground mb-1">
                      {item.value}
                    </p>
                    <p className="text-sm text-muted-foreground font-default">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-card border border-border p-8 md:p-12 rounded-lg shadow-lg relative">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/20 rounded-tl-md" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary/20 rounded-tr-md" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary/20 rounded-bl-md" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary/20 rounded-br-md" />

              <div className="mb-8 border-b border-border/50 pb-6">
                <h3 className="text-2xl font-default font-bold text-primary flex items-center gap-3">
                  <Terminal className="size-6" />
                  General Inquiry Form
                </h3>
                <p className="text-muted-foreground font-default text-sm mt-2">
                  Submit documentation or request information from the department.
                </p>
              </div>

              <FormProvider formKey="reach_team_contact" formId="reach_team_contact" sectionName="Reach Our Team" defaultValues={{
              full_name: "",
              email_address: "",
              phone_number: "",
              department: "",
              interest_message: ""
            }} submitText="Submit Official Inquiry" successMessage="Your inquiry has been logged. A department representative will review your submission and respond via the provided contact method.">
                <ExpeditionFormInternal />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}